import {toHTML} from '#src/helper.js';

export const SkipType = {
  NoSkip: 'no-skip',
  SkipCode: 'skip-code',
  SkipHtml: 'skip-html'
};

export default class ProcessingContext {
  #boardName = null;
  #boardNames = [];
  #skipList = [];
  #text = '';

  constructor({boardName, boardNames, text}) {
    this.#boardName = boardName;
    this.#boardNames = boardNames;
    this.#text = text;
  }

  get boardName() {
    return this.#boardName;
  }

  get boardNames() {
    return this.#boardNames;
  }

  get text() {
    return this.#text;
  }

  find(rx, start = 0, isEscapable = false) {
    if (typeof rx === 'string') {
      let ind = this.#text.indexOf(rx, start);

      while (ind >= 0) {
        // eslint-disable-next-line no-loop-func
        const isIn = this.#skipList.some(inf => {
          if (ind >= inf.start && ind < (inf.start + inf.length)) {
            ind = this.#text.indexOf(rx, inf.start + inf.length);

            return true;
          }

          return false;
        });

        if (!isIn) {
          if (isEscapable && this.#isEscaped(ind)) {
            ind = this.#text.indexOf(rx, ind + 1);
          } else {
            // Emulating regexp

            const result = [rx];

            result.index = ind;
            result.input = this.#text;

            return result;
          }
        }
      }
    } else {
      rx.lastIndex = start;

      let match = rx.exec(this.#text);

      while (match) {
        // eslint-disable-next-line no-loop-func
        const isIn = this.#skipList.some(inf => {
          if (match && match.index >= inf.start && match.index < (inf.start + inf.length)) {
            rx.lastIndex = inf.start + inf.length;

            match = rx.exec(this.#text);

            return true;
          }

          return false;
        });

        if (!isIn && match) {
          if (isEscapable && this.#isEscaped(match.index)) {
            rx.lastIndex = match.index + 1;

            match = rx.exec(this.#text);
          } else {
            return match;
          }
        }
      }
    }

    return null;
  }

  insert(start, text, skipType = SkipType.SkipHtml) {
    if (start < 0 || text.length <= 0 || start > this.#text.length) {
      return;
    }

    const info = {
      skipType,
      start,
      length: text.length
    };

    let found = false;

    for (let i = this.#skipList.length - 1; i >= 0; --i) {
      const inf = this.#skipList[i];

      if (start > inf.start) {
        if (skipType !== SkipType.NoSkip) {
          this.#skipList.splice(i + 1, 0, info);
        }

        found = true;

        break;
      }

      inf.start += text.length;
    }

    if (!found && (skipType !== SkipType.NoSkip)) {
      this.#skipList.unshift(info);
    }

    this.#text = this.#text.substr(0, start) + text + this.#text.substr(start);
  }

  isIn(start, length, skipType = SkipType.SkipCode) {
    if (start < 0 || length <= 0 || (start + length) > this.#text.length || (skipType === SkipType.NoSkip)) {
      return false;
    }

    for (let i = 0; i < this.#skipList.length; ++i) {
      const inf = this.#skipList[i];

      if (inf.skipType !== skipType) {
        continue;
      }

      let x = start;

      while (x < start + length) {
        if (x >= inf.start && x <= (inf.start + inf.length)) {
          return true;
        }

        ++x;
      }
    }

    return false;
  }

  replace({correction, length, skipType = SkipType.SkipHtml, start, text}) {
    if (start < 0 || length <= 0 || (text.length < 1) || (length + start) > this.#text.length) {
      return;
    }

    const info = {
      skipType,
      start,
      length: text.length
    };

    const dlength = text.length - length;

    let found = false;

    for (let i = this.#skipList.length - 1; i >= 0; --i) {
      const inf = this.#skipList[i];

      if (start >= inf.start) {
        if (skipType !== SkipType.NoSkip) {
          this.#skipList.splice(i + 1, 0, info);
        }

        found = true;

        break;
      }

      if (inf.start < (start + length)) {
        inf.start -= correction;
      } else {
        inf.start += dlength;
      }
    }

    if (!found && (skipType !== SkipType.NoSkip)) {
      this.#skipList.unshift(info);
    }

    this.#text = this.#text.substr(0, start) + text + this.#text.substr(start + length);
  }

  toHTML() {
    let s = '';
    let last = 0;

    this.#skipList.forEach(inf => {
      s += toHTML(this.#text.substr(last, inf.start - last)) + this.#text.substr(inf.start, inf.length);

      last = inf.start + inf.length;
    });

    return s + toHTML(this.#text.substr(last));
  }

  #isEscaped(pos) {
    if (pos <= 0 || pos >= this.#text.length) {
      return false;
    }

    let n = 0;
    let i = pos - 1;

    while (i >= 0 && this.#text[i] === '\\') {
      ++n;
      --i;
    }

    return (n % 2);
  }
}
